/**
 * Linked bank account (eGIRO) DEV-ONLY harness.
 *
 * Seeds `myGiving.linkedAccounts` with fixture rows so every account state can be
 * viewed on the REAL Manage screen with no bank, no giving API and no network.
 * The point is to see what the shipped UI does with each state — including the
 * states it currently renders nowhere at all.
 *
 * Inert unless enabled from /egiro-states (localStorage flag), and the store
 * hooks additionally refuse to run in a production build. Mirrors the gating in
 * src/utils/next-steps-mock.js.
 */

const EGIRO_HARNESS_KEY = 'hogc_egiro_harness';

// Mirrors src/constants/MyGiving/EGIRO_STATES.js — duplicated rather than
// imported so the fixtures stay readable next to the scenario they belong to.
const S = {
  SUBMITTED: 'Submitted',
  PENDING_APPROVAL: 'Pending_Approval',
  SUCCESS: 'Success',
  FAILED: 'Failed',
  PENDING_REMOVAL: 'Pending_Removal',
  DELETED: 'Deleted',
  LINKED: 'Linked',
};

const account = (id, bankCode, bankName, lastFour, state) => ({
  eGiroMandateId: id,
  boDDARefNo: id,
  bankCode,
  bankName,
  maskedBankAccNumber: `xxxx-xxxx-${lastFour}`,
  state,
});

const DBS = ['DBSSSGSGXXX', 'DBS Bank Ltd'];
const OCBC = ['OCBCSGSGXXX', 'Oversea-Chinese Banking Corporation Ltd'];
const UOB = ['UOVBSGSGXXX', 'United Overseas Bank Ltd'];

/**
 * Each scenario states what the member SHOULD see and what they ACTUALLY see, so
 * a defect is legible from the console without reading the code.
 */
export const EGIRO_SCENARIOS = [
  {
    key: 'none',
    title: 'No accounts',
    accounts: [],
    expected: 'Dashed “Add bank account” empty state.',
    actual: 'Correct.',
    verdict: 'ok',
  },
  {
    key: 'one_linked',
    title: 'One linked account',
    accounts: [account('MND-001', ...DBS, '1234', S.LINKED)],
    expected: 'One selectable row under “Linked accounts”, FAB adds another.',
    actual: 'Correct.',
    verdict: 'ok',
  },
  {
    key: 'two_linked',
    title: 'Two linked accounts',
    accounts: [
      account('MND-001', ...DBS, '1234', S.LINKED),
      account('MND-002', ...OCBC, '5678', S.LINKED),
    ],
    expected: 'Two rows. Selecting one and tapping delete names it in the confirm modal.',
    actual: 'Fixed (ONE-8189) — the modal now names the bank and last four.',
    verdict: 'ok',
  },
  {
    key: 'pending_approval',
    title: 'Awaiting the bank',
    accounts: [
      account('MND-010', ...DBS, '1234', S.SUBMITTED),
      account('MND-011', ...OCBC, '5678', S.PENDING_APPROVAL),
    ],
    expected: 'Both under “Pending to be linked”, each with a way to cancel or retry.',
    actual: 'Fixed (ONE-8184) — each pending row now has a Cancel.',
    verdict: 'ok',
  },
  {
    key: 'pending_removal',
    title: 'Removal in flight',
    accounts: [
      account('MND-001', ...DBS, '1234', S.LINKED),
      account('MND-020', ...OCBC, '5678', S.PENDING_REMOVAL),
    ],
    expected: 'One usable, one greyed under “Pending to be removed”.',
    actual: 'Correct.',
    verdict: 'ok',
  },
  {
    key: 'failed',
    title: 'Bank authorisation FAILED',
    accounts: [account('MND-030', ...UOB, '9012', S.FAILED)],
    expected: 'A “Couldn’t be linked” row explaining what happened, with a retry.',
    actual: 'Fixed (ONE-8181) — renders under “Couldn’t be linked” with a Try again.',
    verdict: 'ok',
  },
  {
    key: 'legacy_success',
    title: 'Legacy “Success” row',
    accounts: [account('MND-040', ...DBS, '1234', S.SUCCESS)],
    expected: 'Treated as linked — Success is the pre-migration name for Linked.',
    actual: 'Fixed (ONE-8186) — accepted as Linked and payable.',
    verdict: 'ok',
  },
  {
    key: 'deleted',
    title: 'Deleted row still returned',
    accounts: [account('MND-050', ...OCBC, '5678', S.DELETED)],
    expected: 'Hidden — this one SHOULD render nothing.',
    actual: 'Correct.',
    verdict: 'ok',
  },
  {
    key: 'mixed',
    title: 'Everything at once',
    accounts: [
      account('MND-001', ...DBS, '1234', S.LINKED),
      account('MND-011', ...OCBC, '5678', S.PENDING_APPROVAL),
      account('MND-020', ...UOB, '3456', S.PENDING_REMOVAL),
      account('MND-030', ...UOB, '9012', S.FAILED),
      account('MND-040', ...DBS, '7890', S.SUCCESS),
    ],
    expected: 'Five rows across four sections.',
    actual: 'Fixed — all five render across four sections.',
    verdict: 'ok',
  },
];

const defaultState = () => ({ enabled: false, scenario: 'none' });

function readState() {
  try {
    const raw = window.localStorage.getItem(EGIRO_HARNESS_KEY);
    return raw ? { ...defaultState(), ...JSON.parse(raw) } : defaultState();
  } catch {
    return defaultState();
  }
}

function writeState(next) {
  try {
    window.localStorage.setItem(EGIRO_HARNESS_KEY, JSON.stringify(next));
  } catch {
    /* storage unavailable — harness simply stays off */
  }
  return next;
}

/** True only when switched on AND not a production build. */
export function isEgiroHarnessActive() {
  if (process.env.VUE_APP_NODE_ENV === 'production') return false;
  return readState().enabled === true;
}

export function getEgiroHarnessState() {
  return readState();
}

export function setEgiroHarness(patch) {
  return writeState({ ...readState(), ...patch });
}

export function resetEgiroHarness() {
  return writeState(defaultState());
}

/** Fixture rows for the active scenario, in the shape GET /e-giro/linked-accounts returns. */
export function harnessAccounts() {
  const { scenario } = readState();
  const found = EGIRO_SCENARIOS.find(s => s.key === scenario);
  return found ? found.accounts.map(a => ({ ...a })) : [];
}

export const EGIRO_HARNESS_STATES = S;
